import { Container, rem } from '@mantine/core';

import type { MantineThemeOverride } from '@mantine/core';

const CONTAINER_SIZES: Record<string, string> = {
  xxs: rem('200px'),
  xs: rem('300px'),
  sm: rem('400px'),
  md: rem('500px'),
  lg: rem('600px'),
  xl: rem('1400px'),
  xxl: rem('1600px'),
};

export const mantineTheme: MantineThemeOverride = {
  fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontFamilyMonospace: '"JetBrains Mono", "Fira Code", monospace',
  headings: {
    fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    sizes: {
      h1: { fontSize: rem('36px'), fontWeight: '700', lineHeight: '1.2' },
      h2: { fontSize: rem('28px'), fontWeight: '600', lineHeight: '1.25' },
      h3: { fontSize: rem('22px'), fontWeight: '600', lineHeight: '1.3' },
      h4: { fontSize: rem('18px'), fontWeight: '600', lineHeight: '1.35' },
      h5: { fontSize: rem('16px'), fontWeight: '600', lineHeight: '1.4' },
      h6: { fontSize: rem('14px'), fontWeight: '600', lineHeight: '1.5' },
    },
  },
  fontSizes: {
    xs: rem('12px'),
    sm: rem('14px'),
    md: rem('15px'),
    lg: rem('16px'),
    xl: rem('18px'),
    '2xl': rem('22px'),
    '3xl': rem('28px'),
    '4xl': rem('36px'),
    '5xl': rem('48px'),
  },
  spacing: {
    '3xs': rem('4px'),
    '2xs': rem('8px'),
    xs: rem('12px'),
    sm: rem('16px'),
    md: rem('20px'),
    lg: rem('24px'),
    xl: rem('32px'),
    '2xl': rem('40px'),
    '3xl': rem('48px'),
  },
  primaryColor: 'indigo',
  primaryShade: { light: 6, dark: 4 },
  cursorType: 'default',
  defaultRadius: 'md',
  colors: {
    dark: [
      '#C1C2C5',
      '#A6A7AB',
      '#909296',
      '#5C5F66',
      '#363B44',
      '#2A2E37',
      '#232730',
      '#1A1D26',
      '#13151D',
      '#0E1017',
    ],
    violet: [
      '#f5f3ff',
      '#ede9fe',
      '#ddd6fe',
      '#c4b5fd',
      '#a78bfa',
      '#8b5cf6',
      '#7c3aed',
      '#6d28d9',
      '#5b21b6',
      '#4c1d95',
    ],
    gray: [
      '#f8fafc',
      '#f1f5f9',
      '#e2e8f0',
      '#cbd5e1',
      '#94a3b8',
      '#64748b',
      '#475569',
      '#334155',
      '#1e293b',
      '#0f172a',
    ],
    indigo: [
      '#eef2ff',
      '#e0e7ff',
      '#c7d2fe',
      '#a5b4fc',
      '#818cf8',
      '#6366f1',
      '#4f46e5',
      '#4338ca',
      '#3730a3',
      '#312e81',
    ],
    green: [
      '#f0fdf4',
      '#dcfce7',
      '#bbf7d0',
      '#86efac',
      '#4ade80',
      '#22c55e',
      '#16a34a',
      '#15803d',
      '#166534',
      '#14532d',
    ],
    red: ['#fef2f2', '#fee2e2', '#fecaca', '#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d'],
    amber: [
      '#fffbeb',
      '#fef3c7',
      '#fde68a',
      '#fcd34d',
      '#fbbf24',
      '#f59e0b',
      '#d97706',
      '#b45309',
      '#92400e',
      '#78350f',
    ],
  },
  components: {
    Container: Container.extend({
      vars: (_, { size, fluid }) => ({
        root: {
          '--container-size': fluid
            ? '100%'
            : size !== undefined && size in CONTAINER_SIZES
              ? CONTAINER_SIZES[size]
              : rem(size),
        },
      }),
    }),
    Paper: {
      defaultProps: {
        p: 'lg',
        radius: 'md',
        withBorder: true,
        shadow: 'xs',
      },
    },
    Card: {
      defaultProps: {
        p: 'lg',
        radius: 'md',
        withBorder: true,
        shadow: 'xs',
      },
    },
    Select: {
      defaultProps: {
        checkIconPosition: 'right',
      },
    },
    Button: {
      defaultProps: {
        size: 'md',
        radius: 'md',
        variant: 'filled',
      },
      styles: {
        label: {
          fontWeight: 600,
          letterSpacing: '-0.01em',
        },
      },
    },
    TextInput: {
      defaultProps: {
        radius: 'md',
      },
    },
    Table: {
      defaultProps: {
        verticalSpacing: 'sm',
        horizontalSpacing: 'md',
      },
    },
    Badge: {
      defaultProps: {
        radius: 'md',
      },
    },
    AppShell: {
      defaultProps: {
        padding: 'lg',
      },
    },
    Modal: {
      defaultProps: {
        radius: 'md',
      },
      styles: {
        content: {
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column' as const,
          overflow: 'hidden',
        },
        header: {
          flexShrink: 0,
          borderBottom: '1px solid var(--mantine-color-default-border)',
        },
        body: {
          flex: 1,
          overflowY: 'auto' as const,
          minHeight: 0,
          paddingTop: 'var(--mantine-spacing-md)',
        },
      },
    },
    Menu: {
      defaultProps: {
        radius: 'md',
      },
    },
  },
  other: {
    style: 'mantine',
  },
};

export default mantineTheme;
